import { z } from "zod";
import { EnrichedUserDataSchema } from "./enriched-user-data.schemas.js";

const _enrichedUserData = EnrichedUserDataSchema.unwrap();

/**
 * Projected, secret-free admin view of enriched user data, keyed by public id.
 * Derived from the shared `EnrichedUserData` domain entity that the backend
 * enriched-user-data type also extends, so `.parse(backendObject)` is safe by
 * construction. The raw provider payload, external id, input data, and internal
 * Mongo id live only on the backend type and are therefore never selected here.
 *
 * Two fields diverge from the entity by design: `linkedInUrl` is widened from
 * the branded personal URL to a plain string for the JSON wire, and
 * `employments` is narrowed to the display-worthy subset — derived from the
 * entity's employment element so the inner value types cannot drift.
 * Timestamps are coerced dates serialized to ISO on the wire.
 */
export const EnrichedUserDataViewSchema = _enrichedUserData
  .pick({
    idPublic: true,
    fullName: true,
    firstName: true,
    lastName: true,
    headline: true,
    bio: true,
    location: true,
    country: true,
    currentTitle: true,
    currentOrganization: true,
    photoUrl: true,
    connectionsCount: true,
    followersCount: true,
    industries: true,
    skills: true,
    enrichmentSource: true,
    enrichmentConfidence: true,
    enrichedAt: true,
  })
  .extend({
    linkedInUrl: z.string().nullable(),
    employments: z.array(
      _enrichedUserData.shape.employments.element.pick({
        companyName: true,
        title: true,
        seniority: true,
        isCurrent: true,
        startDate: true,
        endDate: true,
      }),
    ),
  });

export type EnrichedUserDataView = z.infer<typeof EnrichedUserDataViewSchema>;
